import { useEffect, useMemo, useState } from "react";
import { Resource, Texture } from "pixi.js";
import { Assets } from "@pixi/assets";
import BoundingBox from "../../Domain/Types/BoundingBox.type";
import { useFeatures } from "../../Hooks/useFeatures";
import { GeneIconImageGrp, GeneParkingPlaceIcon, WPInitialText } from "./PlaceIcon.partial";

/**
 * Pixi caches profile photos by URL. Leaving a floor destroys the WebGL texture, and the
 * cache still returns that dead texture, so the photo is blank when the booked floor is shown again.
 */
async function loadPlaceIconTexture(icon: string): Promise<Texture<Resource> | undefined> {
  try {
    const loaded = (await Assets.load(icon)) as Texture<Resource>;
    if (!loaded?.destroyed) {
      return loaded;
    }
    await Assets.unload(icon);
    const reloaded = (await Assets.load(icon)) as Texture<Resource>;
    if (reloaded?.destroyed) {
      return undefined;
    }
    return reloaded;
  } catch {
    return undefined;
  }
}

interface Props {
  placeScale: number;
  placeTypeId: number;
  boundingBox: BoundingBox;
  isSelectable: boolean;
  rotation?: number;
  icon?: string;
  isColleaguesPlace?: boolean;
  /** True in booking select so occupancy colour stays visible around the photo. */
  insetProfilePhoto?: boolean;
}

export function PlaceIcon({
  placeScale,
  placeTypeId,
  boundingBox,
  isSelectable,
  rotation = 0,
  icon,
  isColleaguesPlace,
  insetProfilePhoto = false
}: Readonly<Props>) {
  const [iconImage, setIconImage] = useState<Texture<Resource> | undefined>(undefined);
  const { useParkIconText } = useFeatures();

  const circleRadius = boundingBox.width * placeScale * 0.2 * (isColleaguesPlace ? 2.2 : 1);

  /**
   * memoized place icon.
   * @description avoids recreating the same TextStyle or Text props on every re-render, which is expensive
   */
  const memoizedPlaceIcon = useMemo(() => {
    // display profile image of booking users
    if (iconImage) {
      return (
        <GeneIconImageGrp
          iconImage={iconImage}
          circleRadius={circleRadius}
          rotation={rotation}
          placeTypeId={placeTypeId}
          placeScale={placeScale}
          boundingBox={boundingBox}
          isSelectable={isSelectable}
          insetProfilePhoto={insetProfilePhoto}
        />
      );
    }

    // display initial text of booking usersl
    if (icon) {
      const wpPosY = 0 - 16 * placeScale;
      const otherPosY = 0 - (boundingBox.height * placeScale) / 4;

      // normal workplace
      return (
        <WPInitialText
          icon={icon}
          circleRadius={circleRadius}
          isColleaguesPlace={isColleaguesPlace}
          isSelectable={isSelectable}
          posY={placeTypeId === 1 ? wpPosY : otherPosY}
          rotation={rotation}
        />
      );
    }

    // parking place or electric charging place
    if (placeTypeId === 2 || placeTypeId === 4) {
      return (
        <GeneParkingPlaceIcon
          useParkIconText={useParkIconText}
          isSelectable={isSelectable}
          circleRadius={circleRadius}
          placeScale={placeScale}
          boundingBox={boundingBox}
          placeTypeId={placeTypeId}
        />
      );
    }

    return null;
  }, [
    icon,
    isColleaguesPlace,
    circleRadius,
    iconImage,
    placeTypeId,
    insetProfilePhoto,
    boundingBox,
    placeScale,
    isSelectable,
    useParkIconText,
    rotation
  ]);

  useEffect(() => {
    if (!icon || icon.length <= 2) {
      setIconImage(undefined);
      return;
    }

    let cancelled = false;
    const applyLoadedIcon = async () => {
      const image = await loadPlaceIconTexture(icon);
      if (!cancelled) {
        setIconImage(image);
      }
    };
    applyLoadedIcon().catch(() => {
      if (!cancelled) {
        setIconImage(undefined);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [icon]);

  return (
    <>
      {/* memoized place icon component */}
      {memoizedPlaceIcon}
    </>
  );
}
